import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import Axios from "axios";
import { LoginIcon } from "../../assets/svg/LoginIcon";
import { TitleText } from "../../ui/Text";
import Input from "../../ui/Input";
import { SubmitButton } from "../../ui/SubmitButton";
import { USER_URL } from "../../api/urls";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import styled, { keyframes, css } from "styled-components";
import { BASE_API_URL } from "../../utils/app-constants";
import { get } from "../../utils/get";

const OnboardingSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm password is required"),
});

export const Onboarding = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const history = useHistory();
  const { token } = useParams<{ token: string }>();
  const [userInfo, setUserInfo] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      setIsMounted(true);
    }, 100);
  }, []);

  // useEffect(() => {
  //   if (!token) {
  //     toast.error("Invalid or expired onboarding link");
  //     history.push("/login");
  //   }
  // }, [token, history]);

  useEffect(() => {
    if (token) {
      const payload = {
        token,
        should_expire_token: true,
      };
      Axios.post(`${BASE_API_URL}/auth/password/token-authenticate`, payload)
        .then((response) => {
          setUserInfo(response.data);
          toast.success(
            "Your token has been verified successfully. Kindly set your password."
          );
        })
        .catch((error) => {
          toast.error(
            get(error, "response.data.message", "Your token may have expired.")
          );
        });
    }
  }, [token]);

  const handleOnboarding = (values) => {
    let token = get(userInfo, "access_token");
    let userId = get(userInfo, "user.id");
    
    const payload = {
      password: get(values, "password"),
    };
    Axios.put(`${USER_URL}/${userId}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(() => {
        toast.success("Password has been set successfully. Please Log in.");
        history.push("/login");
      })
      .catch((error) => {
        toast.error(get(error, "response.data.message"));
      });
  };

  return (
    <Container>
      <OnboardingContainer isMounted={isMounted}>
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          onSubmit={(values) => handleOnboarding(values)}
          validationSchema={OnboardingSchema}
          validate={(values) => {
            let errors: { confirmPassword?: string } = {};

            // Custom validation logic
            if (values.password !== values.confirmPassword) {
              errors.confirmPassword = "Passwords must match";
            }

            return errors;
          }}
        >
          {({ handleSubmit, errors, touched }) => (
            <OnboardingForm>
              <TitleText style={{ marginBottom: 60, fontSize: 24 }}>
                Set Your Password
              </TitleText>
              <PasswordWrapper>
                <Field
                  type={showPassword ? "text" : "password"}
                  name="password"
                  component={CustomInput}
                  label="New Password"
                />
                {showPassword ? (
                  <VisibilityOff
                    onClick={() => setShowPassword(false)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <Visibility
                    onClick={() => setShowPassword(true)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                )}
              </PasswordWrapper>
              <PasswordWrapper>
                <Field
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  component={CustomInput}
                  label="Confirm Password"
                />
                {showConfirmPassword ? (
                  <VisibilityOff
                    onClick={() => setShowConfirmPassword(false)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <Visibility
                    onClick={() => setShowConfirmPassword(true)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "69%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                  />
                )}
              </PasswordWrapper>
              <AnimatedButton
                text="Set Password"
                type="submit"
                onClick={() => handleSubmit()}
              />
            </OnboardingForm>
          )}
        </Formik>
        <LoginIcon />
      </OnboardingContainer>
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  display: flex;
  min-height: 100%;
  height: 100vh;
  background-color: #f5f6fb;
`;

const OnboardingContainer = styled.div<{ isMounted: boolean }>`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 5em;
  opacity: ${({ isMounted }) => (isMounted ? 1 : 0)};
  ${({ isMounted }) =>
    isMounted &&
    css`
      animation: ${fadeIn} 0.5s ease-out;
    `}
`;

const OnboardingForm = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 2em;
  width: 60%;
  height: 70%;
  border-radius: 30px;
`;

const FieldWrapper = styled.div`
  margin-bottom: 15px;
`;

const PasswordWrapper = styled(FieldWrapper)`
  position: relative;
`;

const AnimatedButton = styled(SubmitButton)`
  width: 100%;
  align-self: flex-end;
  margin-top: 30px;
  margin-bottom: 30px;
  background-color: #01a8ce;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: #018ab0;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const focusIn = keyframes`
  from {
    border-color: #ccc;
  }
  to {
    border-color: #01a8ce;
  }
`;

const AnimatedInput = styled(Input)`
  &:focus {
    animation: ${focusIn} 0.3s forwards;
  }
`;

const CustomInput = styled(Input)`
  width: 100%;
  padding-right: 2.5rem;
`;

const fadeInError = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  animation: ${fadeInError} 0.2s ease-in;
  padding-left: 0.5rem;
`;

export default Onboarding;
